/**
 * [T270-PY] Pure color math for the Day-of-Week heatmap.
 *
 * Implements WCAG 2.2 relative luminance and contrast ratio, plus source-over
 * RGB compositing so the heatmap can render solid cell backgrounds instead of
 * relying on whole-element opacity (which the design spec rejects because it
 * also fades the value text and focus ring).
 *
 * References:
 *   - https://www.w3.org/TR/WCAG22/relative-luminance.html
 *   - https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html
 *
 * Named exports are intentionally narrow and dependency-free so the unit
 * contract (`testify/unit/frontend/dayOfWeekRedesign.test.ts`) can require
 * them directly.
 *
 * @module lib/heatmapContrast
 */

/** An immutable RGB triplet with channels in the [0, 255] range. */
export type RgbColor = readonly [number, number, number];

/** Source-over RGB compositing. */
export function compositeRgb(
  foreground: RgbColor,
  background: RgbColor,
  alpha: number,
): RgbColor {
  const a = clampUnit(alpha);
  const inv = 1 - a;
  return [
    foreground[0] * a + background[0] * inv,
    foreground[1] * a + background[1] * inv,
    foreground[2] * a + background[2] * inv,
  ];
}

/** WCAG 2.2 contrast ratio between two colors. */
export function contrastRatio(foreground: RgbColor, background: RgbColor): number {
  const l1 = relativeLuminance(foreground);
  const l2 = relativeLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick the first candidate foreground that meets the minimum contrast ratio
 * against `background`. Falls back to the highest-contrast candidate when none
 * of the candidates meet the threshold (e.g. when the background is the
 * mid-luminance blue produced by the neutral token at 0.4 intensity).
 */
export function selectReadableForeground(
  background: RgbColor,
  candidates: readonly RgbColor[],
  minimumRatio = 4.5,
): RgbColor {
  let best: RgbColor | null = null;
  let bestRatio = -Infinity;
  for (const candidate of candidates) {
    const ratio = contrastRatio(candidate, background);
    if (ratio >= minimumRatio) {
      return candidate;
    }
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
  }
  if (best !== null) return best;
  // Defensive: empty candidates list. Return black so the caller never sees
  // an undefined value, matching the contract's expectation of a stable RGB
  // tuple.
  if (candidates.length > 0) return candidates[0];
  return [0, 0, 0];
}

/** WCAG 2.2 relative luminance for an sRGB color. */
export function relativeLuminance(color: RgbColor): number {
  return (
    0.2126 * linearizeChannel(color[0]) +
    0.7152 * linearizeChannel(color[1]) +
    0.0722 * linearizeChannel(color[2])
  );
}

/** Convert an RGB triplet to a CSS `rgb()` string with integer channels. */
export function rgbToCss(color: RgbColor): string {
  return `rgb(${roundChannel(color[0])}, ${roundChannel(color[1])}, ${roundChannel(color[2])})`;
}

/** Theme-aware source colors used by the Day-of-Week heatmap. */
export interface HeatmapThemePalette {
  /** The neutral data-token color the heatmap paints over. */
  neutral: RgbColor;
  /** The opaque card surface behind the heatmap. */
  card: RgbColor;
}

/** Resolve the neutral/card RGB triplet pair for the active theme. */
export function resolveHeatmapPalette(theme: 'light' | 'dark'): HeatmapThemePalette {
  if (theme === 'dark') {
    return {
      neutral: [96, 165, 250],
      card: [15, 23, 42],
    };
  }
  return {
    neutral: [59, 130, 246],
    card: [255, 255, 255],
  };
}

/** Compose a cell background + readable foreground for a given intensity. */
export function composeHeatmapCell(
  palette: HeatmapThemePalette,
  intensity: number,
  candidates: readonly RgbColor[],
  minimumRatio = 4.5,
): { background: RgbColor; foreground: RgbColor } {
  const background = compositeRgb(palette.neutral, palette.card, intensity);
  const foreground = selectReadableForeground(background, candidates, minimumRatio);
  return { background, foreground };
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function linearizeChannel(value: number): number {
  const normalized = value / 255;
  if (normalized <= 0.04045) {
    return normalized / 12.92;
  }
  return ((normalized + 0.055) / 1.055) ** 2.4;
}

function clampUnit(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function roundChannel(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 255) return 255;
  return Math.round(value);
}


// ──────────────────────────────────────────────────────────────────────────
// Currency-aware intensity bins
// ──────────────────────────────────────────────────────────────────────────

/**
 * Per-currency intensity thresholds. Each currency has 5 ascending bins chosen
 * to match the natural franction sizes in that currency's psychology (IDR uses
 * 50k/100k increments; USD uses $1/$10/$100; JPY uses 100/1000/10000). The
 * function returns the BIN INDEX (0..5) for a given amount, where 0 means empty
 * (rendered neutral) and 5 means max intensity.
 */
const CURRENCY_INTENSITY_THRESHOLDS = new Map<string, number[]>([
  ['IDR', [5000, 20000, 50000, 100000, 500000]],
  ['USD', [1, 5, 20, 50, 200]],
  ['EUR', [1, 5, 20, 50, 200]],
  ['GBP', [1, 5, 20, 50, 200]],
  ['AUD', [1, 5, 20, 50, 200]],
  ['CAD', [1, 5, 20, 50, 200]],
  ['SGD', [1, 5, 20, 50, 200]],
  ['MYR', [1, 5, 20, 50, 200]],
  ['JPY', [100, 500, 2000, 5000, 20000]],
  ['INR', [10, 50, 200, 500, 2000]],
  ['CNY', [10, 50, 200, 500, 2000]],
  ['KRW', [1000, 5000, 20000, 50000, 200000]],
  ['SGD', [1, 5, 20, 50, 200]],
  ['HKD', [10, 50, 200, 500, 2000]],
  ['THB', [10, 50, 200, 500, 2000]],
  ['PHP', [10, 50, 200, 500, 2000]],
  ['VND', [10000, 50000, 100000, 500000, 2000000]],
  ['TWD', [10, 50, 200, 500, 2000]],
]);
const DEFAULT_INTENSITY_THRESHOLDS = [1, 10, 100, 1000, 10000];

/**
 * Map a cell amount to a 0..6 intensity bin. Bin 0 is reserved for empty cells
 * (cellTotal <= 0); bins 1..5 are filled-cell bins; bin 6 is the "huge" overflow
 * bin for amounts above the largest threshold (rare but possible).
 *
 * The bin number is monotonically non-decreasing with amount, so equal or
 * larger amounts always fall in the same or higher bin.
 */
export function getColorIntensity(amount: number, currency: string | undefined): number {
  if (amount <= 0) return 0;
  const thresholds = currency ? CURRENCY_INTENSITY_THRESHOLDS.get(currency) : undefined;
  const bins = thresholds ?? DEFAULT_INTENSITY_THRESHOLDS;
  for (let i = 0; i < bins.length; i++) {
    if (amount < bins[i]) return i + 1;
  }
  return bins.length + 1;
}

/**
 * Convert a bin index (0..6) to an intensity value in the [0.1, 0.7] range the
 * Day-of-Week heatmap uses. Bin 0 is the empty case (returns 0; the caller
 * should render the cell with greyscale styling instead of using this value).
 */
export function binToIntensity(bin: number): number {
  if (bin <= 0) return 0;
  if (bin >= 6) return 0.7;
  // 1 -> 0.1, 2 -> 0.22, 3 -> 0.34, 4 -> 0.46, 5 -> 0.58, 6 -> 0.7
  return 0.1 + (bin - 1) * 0.12;
}

/**
 * One-shot convenience: amount -> intensity (0..0.7) for the given currency.
 * Use this in the heatmap cell render instead of the linear
 * `cellTotal / maxCellTotal` heuristic so cells are categorized by the
 * currency's natural franction sizes instead of relative to the heatmap's max.
 */
export function currencyIntensity(amount: number, currency: string | undefined): number {
  return binToIntensity(getColorIntensity(amount, currency));
}
